import { assignIfExists, assignProps } from '@/utils/resultsHelper';
import { responseError } from '@/utils/errors';
import { saveStates, states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import updateMultiplePanels from '@/utils/panels';
import {
  closeDatasetSettingsState,
  confirmCatalogueChange,
  confirmDatasetWizardChange,
  confirmWorkflowChange,
  datasetSettingsFields,
  editCohortSettings,
  findDatasetSettings,
  settingsPanels,
} from './state';
import actions, { datasetSettingsLifeCycle } from '../constants';

const hideCatalogueConfirmation = withDraft((draft) => { draft.confirmCatalogue = confirmCatalogueChange(); });

const hideDatasetTypeWizardConfirmation = withDraft((draft) => {
  draft.confirmWizard = confirmDatasetWizardChange();
});

const hideWorkflowConfirmation = withDraft((draft) => { draft.confirmWorkflow = confirmWorkflowChange(); });

const hideEditCohortSettings = withDraft((draft) => { draft.cohortSettings = editCohortSettings(); });

const updateCohortSettingsProperties = withDraft((draft, { properties }) => {
  assignIfExists(draft.cohortSettings, properties);
});

const showCatalogueConfirmation = withDraft((draft, action) => {
  assignIfExists(draft.confirmCatalogue, { ...action, open: true });
});

const showDatasetTypeWizardConfirmation = withDraft((draft, action) => {
  assignIfExists(draft.confirmWizard, { ...action, open: true });
});

const showWorkflowConfirmation = withDraft((draft, {
  category,
  code,
  name,
  workflow,
}) => {
  draft.confirmWorkflow.open = true;
  draft.confirmWorkflow.code = code;
  draft.confirmWorkflow.category = category;
  draft.confirmWorkflow.workflow = workflow;
  draft.confirmWorkflow.title = name;
});

const showEditCohortSettings = withDraft((draft, action) => {
  const item = findDatasetSettings(draft, action);

  assignIfExists(draft.cohortSettings, {
    ...action,
    open: true,
    minimum_cohort_size: item.minimum_cohort_size,
  });
});

const updateDatasetSettingsProperties = withDraft((draft, { category, code, properties }) => {
  const item = findDatasetSettings(draft, { category, code });
  assignProps(item, properties, datasetSettingsLifeCycle, true);
});

const fetchDatasetSettings = withDraft((draft, { category, code }) => {
  const item = findDatasetSettings(draft, { category, code });

  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  } else {
    draft.settings[category].results.push({
      code,
      readErrors: [],
      readStatus: states.PENDING,
      ...datasetSettingsFields(),
      ...settingsPanels(),
    });
  }
});

const fetchDatasetSettingsSuccess = withDraft((draft, { category, code, result }) => {
  const item = findDatasetSettings(draft, { category, code });
  const props = {
    ...result,
    readErrors: [],
    readStatus: states.SUCCESS,
    ...saveStates,
    ...settingsPanels(),
  };

  if (item) {
    assignProps(item, props);
  } else {
    draft.settings[category].results.push({
      code,
      ...datasetSettingsFields(),
      ...props,
    });
  }
});

const fetchDatasetSettingsError = withDraft((draft, action) => {
  const item = findDatasetSettings(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const updateDatasetSettingsSuccess = withDraft((draft, { category, code, result }) => {
  const item = findDatasetSettings(draft, { category, code });

  if (item) {
    assignIfExists(item, { ...result, ...saveStates });
  }
  draft.cohortSettings = editCohortSettings();
});

const updateDatasetSettingsError = withDraft((draft, action) => {
  const item = findDatasetSettings(draft, action);

  if (item) {
    assignIfExists(item, {
      saveErrors: [responseError(action)],
      saveStatus: states.ERROR,
    });
  }
});

const switchDatasetType = withDraft((draft, { category, code, templateChanged }) => {
  const item = findDatasetSettings(draft, { category, code });

  draft.confirmWizard = confirmDatasetWizardChange();
  if (item && templateChanged) {
    item.saveStatus = states.PENDING;
  }
});

const updateDatasetSettings = withDraft((draft, action) => {
  const item = findDatasetSettings(draft, action);

  draft.confirmCatalogue = confirmCatalogueChange();
  draft.confirmWorkflow = confirmWorkflowChange();
  if (item) {
    item.saveStatus = states.PENDING;
  }
});

const updateDatasetSettingsPanels = withDraft((draft, action) => {
  const item = findDatasetSettings(draft, action);
  item.panels[action.panel] = updateMultiplePanels(item, action);
});

export default {
  [actions.CLOSE_DATASET_SETTINGS]: closeDatasetSettingsState,
  [actions.FETCH_DATASET_SETTINGS]: fetchDatasetSettings,
  [actions.FETCH_DATASET_SETTINGS_ERROR]: fetchDatasetSettingsError,
  [actions.FETCH_DATASET_SETTINGS_SUCCESS]: fetchDatasetSettingsSuccess,
  [actions.HIDE_CATALOGUE_CONFIRMATION]: hideCatalogueConfirmation,
  [actions.HIDE_DATASET_TYPE_WIZARD_CONFIRMATION]: hideDatasetTypeWizardConfirmation,
  [actions.HIDE_EDIT_COHORT_SETTINGS]: hideEditCohortSettings,
  [actions.HIDE_WORKFLOW_CONFIRMATION]: hideWorkflowConfirmation,
  [actions.SHOW_CATALOGUE_CONFIRMATION]: showCatalogueConfirmation,
  [actions.SHOW_DATASET_TYPE_WIZARD_CONFIRMATION]: showDatasetTypeWizardConfirmation,
  [actions.SHOW_EDIT_COHORT_SETTINGS]: showEditCohortSettings,
  [actions.SHOW_WORKFLOW_CONFIRMATION]: showWorkflowConfirmation,
  [actions.SWITCH_DATASET_TYPE]: switchDatasetType,
  [actions.TOGGLE_DATASET_SETTING]: updateDatasetSettings,
  [actions.UPDATE_COHORT_SETTINGS_PROPERTIES]: updateCohortSettingsProperties,
  [actions.UPDATE_DATASET_SETTINGS]: updateDatasetSettings,
  [actions.UPDATE_DATASET_SETTINGS_ERROR]: updateDatasetSettingsError,
  [actions.UPDATE_DATASET_SETTINGS_PANELS]: updateDatasetSettingsPanels,
  [actions.UPDATE_DATASET_SETTINGS_PROPERTIES]: updateDatasetSettingsProperties,
  [actions.UPDATE_DATASET_SETTINGS_SUCCESS]: updateDatasetSettingsSuccess,
};
